import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Leaf, ShieldCheck, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_LABELS = {
  waste_segregation: 'Separates wet/dry waste',
  avoid_plastic: 'Avoids single-use plastic',
  led_energy: 'LED / energy-saving',
  water_conservation: 'Water conservation',
  local_employment: 'Employs locals',
  local_vendors: 'Buys from local vendors',
  grown_on_property: 'Grows produce on property',
  local_food: 'Serves local food',
  cultural_experiences: 'Offers cultural experiences',
  responsible_behaviour: 'Educates guests on customs',
};

const SustainabilityReviewBlock = ({ property, onUpdated }) => {
  const s = property?.sustainability;
  const [submitting, setSubmitting] = useState(false);
  if (!s || (!s.answers || Object.keys(s.answers).length === 0)) return null;

  const yesItems = Object.entries(s.answers).filter(([, v]) => v === true);
  const score = s.score ?? yesItems.length;
  const selfTier = s.self_tier;
  const currentTier = s.verified_tier;

  const handleVerify = async (tier) => {
    const token = localStorage.getItem('adminToken');
    if (!token) { toast.error('Admin session expired'); return; }
    setSubmitting(true);
    try {
      const propertyId = property._id || property.id;
      const res = await axios.post(`${BACKEND_URL}/api/admin/sustainability/verify`, {
        token,
        property_id: propertyId,
        verified_tier: tier,
      });
      if (res.data?.success) {
        toast.success(tier ? `Sustainability tier set to ${tier.toUpperCase()}` : 'Sustainability cleared');
        onUpdated?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3" data-testid="sustainability-review-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Leaf className="h-4 w-4 text-green-700" />
          <span className="text-sm font-semibold text-green-900">
            Sustainability Self-Assessment
          </span>
          <span className="text-xs text-green-700">
            Host: {selfTier ? `${selfTier.toUpperCase()} (${score}/10)` : 'No tier'}
          </span>
        </div>
        {currentTier && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-green-300 text-green-800">
            ✓ Verified: {currentTier.toUpperCase()}
          </span>
        )}
      </div>

      <ul className="text-xs text-gray-700 space-y-0.5 mb-2 max-h-32 overflow-y-auto">
        {yesItems.map(([id]) => {
          const proof = s.proof_photos?.[id];
          return (
            <li key={id} className="flex items-center justify-between gap-2">
              <span>✓ {QUESTION_LABELS[id] || id}</span>
              {proof ? (
                <a
                  href={proof.startsWith('http') ? proof : `${BACKEND_URL}${proof}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] text-[#7CB342] hover:underline inline-flex items-center gap-0.5"
                  data-testid={`sust-review-proof-${id}`}
                >
                  proof <ExternalLink className="h-2.5 w-2.5" />
                </a>
              ) : (
                <span className="text-[10px] text-gray-400">no proof</span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-600 mr-1">Verify as:</span>
        {['bronze', 'silver', 'gold'].map((t) => (
          <Button
            key={t}
            type="button"
            size="sm"
            variant={currentTier === t ? 'default' : 'outline'}
            disabled={submitting}
            onClick={() => handleVerify(t)}
            className={`h-7 px-2 text-xs ${currentTier === t ? 'bg-[#7CB342] hover:bg-[#6da036] text-white' : ''}`}
            data-testid={`sust-verify-${t}`}
          >
            <ShieldCheck className="h-3 w-3 mr-1" />
            {t.toUpperCase()}
          </Button>
        ))}
        {currentTier && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={submitting}
            onClick={() => handleVerify(null)}
            className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
            data-testid="sust-verify-clear"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default SustainabilityReviewBlock;
